import DOMPurify from "dompurify";

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} dirty - The potentially unsafe HTML string
 * @param {object} config - Optional DOMPurify configuration
 * @returns {string} - Sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (dirty, config = {}) => {
    if (!dirty || typeof dirty !== "string") {
        return "";
    }

    // Default configuration - allows common HTML tags but removes scripts
    const defaultConfig = {
        ALLOWED_TAGS: [
            "p",
            "br",
            "strong",
            "em",
            "u",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "ul",
            "ol",
            "li",
            "a",
            "span",
            "div",
            "table",
            "thead",
            "tbody",
            "tr",
            "th",
            "td",
            "img",
            "blockquote",
            "code",
            "pre",
        ],
        ALLOWED_ATTR: [
            "href",
            "target",
            "rel",
            "style",
            "class",
            "id",
            "src",
            "alt",
            "title",
        ],
        ALLOW_DATA_ATTR: false,
        ...config,
    };

    return DOMPurify.sanitize(dirty, defaultConfig);
};

/**
 * Sanitizes HTML content with stricter rules (no links, no images)
 * @param {string} dirty - The potentially unsafe HTML string
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtmlStrict = (dirty) => {
    return sanitizeHtml(dirty, {
        ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "span", "div"],
        ALLOWED_ATTR: ["style", "class"],
    });
};

/**
 * Strips all HTML tags and returns plain text
 * @param {string} dirty - The potentially unsafe HTML string
 * @returns {string} - Plain text without any HTML
 */
export const stripHtml = (dirty) => {
    if (!dirty || typeof dirty !== "string") {
        return "";
    }
    return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [] });
};
